import { useApi } from '@backstage/core-plugin-api';
import { isPairingPurpose } from '@internal/spassu-code-contract';
import {
  type Dispatch,
  useCallback,
  useEffect,
  useReducer,
  useRef,
} from 'react';
import {
  controlPlaneApiRef,
  ControlPlaneApiError,
  type ControlPlaneApi,
} from '../api';
import {
  initialOnboardingState,
  type OnboardingAction,
  onboardingReducer,
} from './controlPlaneOnboardingState';

function errorMessage(error: unknown): string {
  if (
    error instanceof ControlPlaneApiError &&
    error.code === 'ONBOARDING_ACTIVE_SESSION_CONFLICT'
  ) {
    return 'Esta atividade já possui uma conexão ativa. Revogue a credencial atual antes de gerar outro código.';
  }
  return error instanceof Error ? error.message : 'A conexão falhou.';
}

function publicErrorCode(error: unknown) {
  return error instanceof ControlPlaneApiError ? error.code : undefined;
}

function isAbort(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

function idempotencyKey(operation: string): string {
  const randomId = globalThis.crypto?.randomUUID?.();
  return `${operation}-${randomId ?? `${Date.now()}-${Math.random()}`}`;
}

function useInitialState(
  api: ControlPlaneApi,
  activityId: string,
  dispatch: Dispatch<OnboardingAction>,
) {
  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    dispatch({ type: 'loading' });
    api
      .getOnboardingState(activityId, controller.signal)
      .then(value => !cancelled && dispatch({ type: 'loaded', value }))
      .catch(error => {
        if (!cancelled && !isAbort(error)) {
          dispatch({
            type: 'error',
            message: errorMessage(error),
            code: publicErrorCode(error),
          });
        }
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [activityId, api, dispatch]);
}

function useOperationController() {
  const ref = useRef<AbortController | null>(null);
  useEffect(() => () => ref.current?.abort(), []);
  return useCallback(() => {
    ref.current?.abort();
    ref.current = new AbortController();
    return ref.current;
  }, []);
}

function dispatchError(
  dispatch: Dispatch<OnboardingAction>,
  error: unknown,
): void {
  if (isAbort(error)) return;
  dispatch({
    type: 'error',
    message: errorMessage(error),
    code: publicErrorCode(error),
  });
}

function useGeneratePairing(
  api: ControlPlaneApi,
  activityId: string,
  dispatch: Dispatch<OnboardingAction>,
  controller: ReturnType<typeof useOperationController>,
) {
  return useCallback(
    async (requested?: unknown) => {
      const purpose = isPairingPurpose(requested) ? requested : undefined;
      dispatch({
        type: 'operation',
        operation: 'pairing',
        announcement: 'Gerando código de pareamento válido por 10 minutos.',
      });
      const operation = controller();
      try {
        const value = await api.createOnboardingPairing(
          activityId,
          idempotencyKey('pairing'),
          purpose,
          operation.signal,
        );
        dispatch({ type: 'pairing', value });
      } catch (error) {
        dispatchError(dispatch, error);
      }
    },
    [activityId, api, controller, dispatch],
  );
}

function useResumeAttempt(
  api: ControlPlaneApi,
  activityId: string,
  dispatch: Dispatch<OnboardingAction>,
  controller: ReturnType<typeof useOperationController>,
) {
  return useCallback(
    async (sessionId: string) => {
      dispatch({
        type: 'operation',
        operation: 'resuming',
        announcement: 'Gerando nova autorização para retomar a conexão.',
      });
      try {
        const value = await api.resumeOnboardingAttempt(
          activityId,
          sessionId,
          idempotencyKey('resume'),
          controller().signal,
        );
        dispatch({ type: 'pairing', value });
      } catch (error) {
        dispatchError(dispatch, error);
      }
    },
    [activityId, api, controller, dispatch],
  );
}

function useRevokeSession(
  api: ControlPlaneApi,
  activityId: string,
  dispatch: Dispatch<OnboardingAction>,
  controller: ReturnType<typeof useOperationController>,
) {
  return useCallback(
    async (sessionId: string) => {
      dispatch({
        type: 'operation',
        operation: 'revoking',
        announcement: 'Revogando a credencial.',
      });
      try {
        const value = await api.revokeOnboardingSession(
          activityId,
          sessionId,
          idempotencyKey('revoke'),
          controller().signal,
        );
        dispatch({ type: 'revoked', value });
      } catch (error) {
        dispatchError(dispatch, error);
      }
    },
    [activityId, api, controller, dispatch],
  );
}

export function useControlPlaneOnboarding(activityId: string) {
  const api = useApi(controlPlaneApiRef);
  const [state, dispatch] = useReducer(
    onboardingReducer,
    initialOnboardingState,
  );
  useInitialState(api, activityId, dispatch);
  const controller = useOperationController();
  const generate = useGeneratePairing(api, activityId, dispatch, controller);
  const resume = useResumeAttempt(api, activityId, dispatch, controller);
  const revoke = useRevokeSession(api, activityId, dispatch, controller);
  const announce = useCallback(
    (message: string) => dispatch({ type: 'announce', message }),
    [],
  );
  return { ...state, generate, resume, revoke, announce };
}

export type ControlPlaneOnboardingView = ReturnType<
  typeof useControlPlaneOnboarding
>;
